import { cn } from '@/lib/utils';

export interface GentleLoadingScreenProps {
  /** The reassuring headline, e.g. "Setting up your workspace…". */
  title: string;
  /** Optional muted line under the title, e.g. "Getting everything ready.". */
  subtitle?: string;
  /**
   * 0–100 perceived-progress for a determinate bar (the create-campaign wizard,
   * which floors its own submit duration). Omit for an indeterminate load of
   * unknown length (the Close hub's data fetch) — the bar animates instead.
   */
  progress?: number;
  /** Forwarded to `data-testid`; the inner bar gets `${testId}-bar`. */
  testId?: string;
  /** Extra classes merged onto the outer wrapper. */
  className?: string;
}

/**
 * The calm "we're getting things ready" card first shown by the create-campaign
 * wizard while it provisions a workspace. Extracted here so any slow-loading
 * surface can reuse the same gentle treatment — a centered card with a
 * reassuring headline and a slim bar — instead of a cold empty state or a bare
 * "Loading…" that reads as "something is wrong."
 *
 * Presentational only: the caller decides WHEN to render it. `role="status"`
 * announces the headline to assistive tech while data loads.
 */
export function GentleLoadingScreen({
  title,
  subtitle,
  progress,
  testId,
  className,
}: GentleLoadingScreenProps) {
  const isDeterminate = progress != null;

  return (
    <div
      role="status"
      data-testid={testId}
      className={cn('mx-auto w-full max-w-md', className)}
    >
      <div className="flex flex-col items-center gap-5 rounded-xl border bg-card p-10 text-center shadow-sm">
        <div>
          <h1 className="text-lg font-semibold text-foreground">{title}</h1>
          {subtitle && (
            <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>
          )}
        </div>
        <div className="h-1 w-full max-w-xs overflow-hidden rounded-full bg-muted">
          {isDeterminate ? (
            <div
              data-testid={testId ? `${testId}-bar` : undefined}
              className="h-full rounded-full bg-primary transition-[width] duration-[1500ms] ease-out"
              style={{ width: `${progress}%` }}
            />
          ) : (
            // Indeterminate: a soft, breathing bar. Uses the built-in
            // `animate-pulse` so no new keyframe is needed and the wait reads as
            // "loading," not "stuck."
            <div
              data-testid={testId ? `${testId}-bar` : undefined}
              className="h-full w-full rounded-full bg-primary/60 animate-pulse"
            />
          )}
        </div>
      </div>
    </div>
  );
}
